/**
 * The guide anchor registry: every feature's anchors and named state reads
 * (shared/features/<feature>.anchors.ts), collected once. Kernel and
 * contract: shared/guideAnchor.ts; overview: docs/architecture/guide-anchors.md.
 *
 * The generated catalogue (GUIDE_ANCHOR_CATALOGUE_PATH) is this registry as
 * JSON, committed and drift-checked in preflight
 * (scripts/generate-guide-anchor-catalog.mjs --check). Admin reads it from
 * the operations repository at a commit to check that a guide names only
 * anchors that commit has.
 *
 * A feature with anchors adds one import pair below.
 */

import type { GuideAnchor, GuideStateRead } from './guideAnchor'
import { guideAnchors as msgraphAnchors, guideStateReads as msgraphReads } from './features/msgraph.anchors'
import { guideAnchors as routinesAnchors, guideStateReads as routinesReads } from './features/routines.anchors'
import { guideAnchors as complianceCalendarAnchors, guideStateReads as complianceCalendarReads } from './features/compliance_calendar.anchors'

/** The catalogue's path in the operations repository. Stable: Admin reads it at a commit. */
export const GUIDE_ANCHOR_CATALOGUE_PATH = 'dashboard/shared/guide-anchors.catalog.generated.json'

type DeclaredAnchor = GuideAnchor & { readonly feature: string }

const has = (record: object, name: string) => Object.prototype.hasOwnProperty.call(record, name)

function collect<T>(kind: string, ...parts: ReadonlyArray<Readonly<Record<string, T>>>): Record<string, T> {
  const out: Record<string, T> = {}
  for (const part of parts) {
    for (const [name, value] of Object.entries(part)) {
      if (has(out, name)) throw new Error(`guide ${kind} "${name}" is declared twice`)
      out[name] = value
    }
  }
  return out
}

// One spread per feature keeps every name as a type; collect() refuses a
// name two features declare (a spread would silently keep the last).
const reads = { ...msgraphReads, ...routinesReads, ...complianceCalendarReads }
const anchors = { ...msgraphAnchors, ...routinesAnchors, ...complianceCalendarAnchors }
collect<GuideStateRead>('state read', msgraphReads, routinesReads, complianceCalendarReads)
collect<DeclaredAnchor>('anchor', msgraphAnchors, routinesAnchors, complianceCalendarAnchors)

// A visible/filled completion that names another anchor must name one this
// release declares; a typo fails to load (and the worker typecheck, which
// imports this registry).
for (const [name, anchor] of Object.entries(anchors)) {
  const completion = (anchor as DeclaredAnchor).completion
  if ((completion.kind === 'visible' || completion.kind === 'filled') && completion.anchor !== undefined) {
    if (!Object.prototype.hasOwnProperty.call(anchors, completion.anchor)) {
      throw new Error(`guide anchor "${name}": unknown observed anchor "${completion.anchor}"`)
    }
  }
}

/** Every declared anchor name. A name outside this union fails to type-check. */
export type GuideAnchorName = keyof typeof anchors
export type GuideStateReadName = keyof typeof reads

export const GUIDE_ANCHORS: Readonly<Record<GuideAnchorName, DeclaredAnchor>> = anchors
export const GUIDE_STATE_READS: Readonly<Record<GuideStateReadName, GuideStateRead>> = reads

export function isGuideAnchorName(name: unknown): name is GuideAnchorName {
  return typeof name === 'string' && has(GUIDE_ANCHORS, name)
}

/**
 * The DOM attributes that mark a declared anchor's element. Throws for an
 * undeclared name, a keyed anchor without its key, or a key on an unkeyed
 * anchor — the frontend helper turns that into a test/dev failure and, in a
 * production build, into no marker at all.
 */
export function guideAnchorAttributes(name: string, key?: string | null): Record<string, string> {
  if (!isGuideAnchorName(name)) throw new Error(`Unknown guide anchor "${name}": declare it in shared/features/<feature>.anchors.ts`)
  const anchor = GUIDE_ANCHORS[name]
  const hasKey = key !== undefined && key !== null && key !== ''
  if (anchor.keyed && !hasKey) throw new Error(`Guide anchor "${name}" is keyed: pass the item's key`)
  if (!anchor.keyed && hasKey) throw new Error(`Guide anchor "${name}" is not keyed`)
  return hasKey ? { 'data-guide-anchor': name, 'data-guide-anchor-key': String(key) } : { 'data-guide-anchor': name }
}

/**
 * A declared anchor's help line — the one text both the page (next to the
 * control) and Guide (in its answers) use. Throws for an undeclared name or
 * an anchor without a help line, so a page cannot silently render nothing.
 */
export function guideAnchorHelp(name: string): string {
  if (!isGuideAnchorName(name)) throw new Error(`Unknown guide anchor "${name}": declare it in shared/features/<feature>.anchors.ts`)
  const help = GUIDE_ANCHORS[name].help
  if (!help) throw new Error(`Guide anchor "${name}" declares no help line`)
  return help
}

/** The catalogue: the registry as data (no functions), sorted by name. */
export function guideAnchorCatalogue() {
  const byName = <T>(entries: Array<[string, T]>) => entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return {
    $comment: `GENERATED by dashboard/scripts/generate-guide-anchor-catalog.mjs from shared/guideAnchorRegistry.ts — do not edit. Path is stable (${GUIDE_ANCHOR_CATALOGUE_PATH}): Admin reads it at an operations commit. Contract: docs/architecture/guide-anchors.md.`,
    schemaVersion: 1,
    anchors: byName(Object.entries(GUIDE_ANCHORS) as Array<[string, DeclaredAnchor]>).map(([name, anchor]) => ({
      name,
      feature: anchor.feature,
      label: anchor.label,
      ...(anchor.help ? { help: anchor.help } : {}),
      page: { path: anchor.page.path, params: anchor.page.params ?? {} },
      keyed: Boolean(anchor.keyed),
      completion: anchor.completion.kind === 'route'
        ? { kind: 'route', path: anchor.completion.path, params: anchor.completion.params ?? {} }
        : anchor.completion,
    })),
    stateReads: byName(Object.entries(GUIDE_STATE_READS) as Array<[string, GuideStateRead]>).map(([name, read]) => ({
      name,
      feature: read.feature,
      label: read.label,
      keyed: Boolean(read.keyed),
      ...(read.items ? { newItems: true } : {}),
      contract: read.contract,
      method: read.method,
      path: read.path,
    })),
  }
}

/** The catalogue file's exact text. */
export function renderGuideAnchorCatalogue(): string {
  return `${JSON.stringify(guideAnchorCatalogue(), null, 2)}\n`
}
